import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { COMPANY } from "@/i18n/translations";
import { calcMonthlyRate, fmtEUR, FINANCE_DEFAULTS } from "@/lib/finance";
import { Calculator, ArrowRight, MessageCircle, RefreshCw } from "lucide-react";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function FinanceCalculator({ vehicle }) {
  const price = vehicle.price;
  const initialDown = vehicle.finance_down ?? Math.max(FINANCE_DEFAULTS.minDown, Math.round((price * FINANCE_DEFAULTS.downPct) / 100) * 100);
  const initialBalloon = vehicle.finance_balloon ?? Math.round((price * FINANCE_DEFAULTS.balloonPct) / 100) * 100;
  const initialTerm = vehicle.finance_term ?? FINANCE_DEFAULTS.termMonths;
  const annualRate = vehicle.finance_rate ?? FINANCE_DEFAULTS.annualRate;

  const [down, setDown] = useState(initialDown);
  const [term, setTerm] = useState(initialTerm);
  const [balloon, setBalloon] = useState(initialBalloon);

  const fin = useMemo(
    () => calcMonthlyRate(price, { down, term, balloon, annualRate }),
    [price, down, term, balloon, annualRate]
  );

  const reset = () => {
    setDown(initialDown); setTerm(initialTerm); setBalloon(initialBalloon);
  };

  const waText = `Hallo DNA Automotive, ich interessiere mich für die Finanzierung des ${vehicle.brand} ${vehicle.model}. Meine Wunsch-Parameter: ${term} Monate Laufzeit, ${fmtEUR(down)} Anzahlung, ${fmtEUR(balloon)} Schlussrate → ${fmtEUR(fin.rate)}/Monat.`;

  return (
    <section id="finanzierung" className="scroll-mt-24" data-testid="finance-calculator">
      <div className="bg-obsidian-surface border border-white/10 p-6 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <Calculator className="w-5 h-5 text-brand-bright" />
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em]">Finanzierungsrechner</p>
        </div>
        <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] mb-3">
          Ihre monatliche Rate
        </h2>
        <p className="text-zinc-400 text-sm md:text-base mb-8 max-w-2xl">
          Passen Sie Anzahlung, Laufzeit und Schlussrate an. Die monatliche Rate berechnet sich sofort – Finanzierung über {fin.partner}.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Slider column */}
          <div className="space-y-8">
            <SliderRow
              label="Anzahlung"
              value={down}
              min={0}
              max={Math.max(1000, Math.round(price * 0.8))}
              step={500}
              onChange={(v) => setDown(clamp(v, 0, price))}
              format={fmtEUR}
              testid="finance-down"
            />
            <SliderRow
              label="Laufzeit"
              value={term}
              min={12}
              max={96}
              step={6}
              onChange={setTerm}
              format={(v) => `${v} Monate`}
              testid="finance-term"
            />
            <SliderRow
              label="Schlussrate"
              value={balloon}
              min={0}
              max={Math.round(price * 0.7)}
              step={500}
              onChange={(v) => setBalloon(clamp(v, 0, price))}
              format={fmtEUR}
              testid="finance-balloon"
            />
            <button
              onClick={reset}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-brand-bright transition-colors"
              data-testid="finance-reset"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Auf Standard zurücksetzen
            </button>
          </div>

          {/* Result column */}
          <div className="bg-obsidian border border-white/10 p-6 md:p-8 flex flex-col">
            <div className="pb-6 border-b border-white/10">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Monatliche Rate</div>
              <motion.div
                key={fin.rate}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="font-heading text-5xl md:text-6xl font-black text-brand-bright leading-none"
                data-testid="finance-monthly-rate"
              >
                {fmtEUR(fin.rate)}
              </motion.div>
              <div className="text-xs text-zinc-500 mt-2">
                bei {annualRate.toFixed(2).replace(".", ",")} % Sollzins gebunden / {annualRate.toFixed(2).replace(".", ",")} % effektiver Jahreszins*
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-y-4 gap-x-6 py-6 text-sm">
              <RowItem k="Fahrzeugpreis" v={fmtEUR(price)} />
              <RowItem k="Anzahlung" v={fmtEUR(fin.down)} />
              <RowItem k="Nettodarlehensbetrag" v={fmtEUR(fin.financingAmount)} />
              <RowItem k="Laufzeit" v={`${fin.term} Monate`} />
              <RowItem k="Schlussrate" v={fmtEUR(fin.balloon)} />
              <RowItem k="Gesamtbetrag" v={fmtEUR(fin.totalRepayment)} highlight />
            </dl>

            <div className="flex flex-col sm:flex-row gap-3 mt-auto pt-6 border-t border-white/10">
              <button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="finance-request"
                className="group flex-1 inline-flex items-center justify-center gap-2 bg-brand text-black text-xs md:text-sm font-bold uppercase tracking-wider px-5 py-4 hover:bg-brand-hover transition-colors"
              >
                Finanzierungsangebot anfragen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(waText)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="finance-whatsapp"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white text-xs md:text-sm font-bold uppercase tracking-wider px-5 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>

        <p className="text-[10px] leading-relaxed text-zinc-500 mt-8 max-w-4xl">
          <strong className="text-zinc-400">* Repräsentatives Beispiel gemäß § 6a PAngV:</strong>{" "}
          Nettodarlehensbetrag {fmtEUR(fin.financingAmount)} · Laufzeit {fin.term} Monate · Sollzinssatz gebunden {annualRate.toFixed(2).replace(".", ",")} % p. a. · effektiver Jahreszins {annualRate.toFixed(2).replace(".", ",")} % · Anzahlung {fmtEUR(fin.down)} · Schlussrate {fmtEUR(fin.balloon)} · monatliche Rate {fmtEUR(fin.rate)} · Gesamtbetrag {fmtEUR(fin.totalRepayment)}. Kreditvermittler (ungebunden, § 655a BGB): {fin.partner}. Bonität vorausgesetzt. Alle Angaben ohne Gewähr, unverbindlich, keine Rechtsberatung.
        </p>
      </div>
    </section>
  );
}

function SliderRow({ label, value, min, max, step, onChange, format, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
        <span className="font-heading text-lg text-brand-bright font-bold" data-testid={`${testid}-value`}>
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-[#2EE06B]"
      />
      <div className="flex justify-between text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

function RowItem({ k, v, highlight = false }) {
  return (
    <>
      <dt className="text-zinc-500 uppercase tracking-wider text-[11px]">{k}</dt>
      <dd className={highlight ? "text-white font-bold text-right" : "text-zinc-200 text-right"}>{v}</dd>
    </>
  );
}
